import React from 'react';
import { useParams } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import Text from '@/components/ui/Text';
import type { ISearchResult } from './types';
import Result, { ImageModel } from './Result';
import { FILTER_CATEGORIES } from './Filters/filterCategories';

const ComponentMap = {
  ImageModel,
  ReferenceMaterialTranslation: Result,
  SectionReadingLevelTranslation: Result,
  Slide: Result,
};

const SearchResult = ({
  body,
  category,
  id,
  image_url,
  is_sysadmin,
  klass,
  klass_id,
  locale,
  path,
  position,
  program,
  program_id,
  published,
  tci_only,
  title,
  location,
  search_id,
  score,
  showScore = false,
  truncateResults = false,
}: ISearchResult) => {
  const filterCategory = FILTER_CATEGORIES.find(filter => filter.value === category)?.label;
  const Component = ComponentMap[klass];
  const { programId } = useParams();
  const showBadges = is_sysadmin && (tci_only || published === false);

  return (
    <div
      aria-label={`Search result ${position}: ${title}`}
      className="tw-flex tw-flex-col card tw-px-4 tw-py-3 tw-my-3 tw-text-base tw-relative"
      role="listitem"
    >
      {(showScore || showBadges) && (
        <div className="tw-absolute tw-top-2 tw-right-2 tw-flex tw-flex-col tw-items-end tw-gap-1">
          <div className="tw-flex tw-gap-1">
            {tci_only && (
              <span className="tw-inline-flex tw-items-center tw-rounded tw-border tw-border-orange tw-bg-light-saffron tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-orange">
                TCI Only
              </span>
            )}
            {published === false && (
              <span className="tw-inline-flex tw-items-center tw-rounded tw-border tw-border-red tw-bg-white tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-red">
                Unpublished
              </span>
            )}
          </div>
          {showScore && (
            <span className="tw-text-sm tw-text-dark-grey">
              Score: {score}
            </span>
          )}
        </div>
      )}
      <Text className="!tw-pb-1" size="sm" variant="body" weight="normal">
        <span dangerouslySetInnerHTML={{ __html: program }} />
      </Text>
      <Text className="!tw-pb-1" size="sm" variant="body" weight="normal">
        <span>{filterCategory}{location ? ` - ${location}` : ''}</span>
      </Text>
      <Text className="tw-text-tci-purple !tw-pb-1" size="sm" variant="body" weight="normal">
        <span>
          <a
            dangerouslySetInnerHTML={{ __html: title }}
            href={path.show}
            target="_blank"
            onClick={() => {
              const data = new FormData();

              data.append('search_id', search_id.toString());
              data.append('klass', klass);
              data.append('klass_id', klass_id);
              data.append('program_id', programId ?? program_id.toString());

              navigator.sendBeacon(Routes.plato_api_convert_path(), data);
            }}
          />
          {path.edit && (
            <a
              aria-label="Edit search result"
              href={path.edit}
              target="_blank"
            >
              <i aria-hidden="true" className="fa fa-pencil tw-ml-1" />
              <span className="sr-only">Edit search result</span>
            </a>
          )}
        </span>
      </Text>
      <Component
        body={body}
        category={category}
        id={id}
        image_url={image_url}
        klass={klass}
        klass_id={klass_id}
        locale={locale}
        location={location}
        path={path}
        position={position}
        program={program}
        program_id={program_id}
        search_id={search_id}
        showScore={showScore}
        title={title}
        truncateResults={truncateResults}
      />
    </div>
  );
};

export default SearchResult;
